/**
 * Higher-Order Component для захисту маршрутів, що вимагають аутентифікації
 * Перевіряє статус аутентифікації та перенаправляє на логін при потребі
 * @component ProtectedRoute
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PUBLIC_ROUTES } from '../constants/routes';
import LoadingSpinner from '../components/LoadingSpinner';
import Layout from '../common/layout/Layout';
import ForbiddenPage from '../pages/ForbiddenPage/ForbiddenPage';

interface ProtectedRouteProps {
  children: React.ReactElement;
  /**
   * Додаткові перевірки доступу (опційно)
   */
  requiredRole?: string[];
  /**
   * Повідомлення при відсутності доступу (опційно)
   */
  accessDeniedMessage?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole = [],
  accessDeniedMessage = 'Недостатньо прав для доступу' 
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  /**
   * Перевіряє, чи має користувач необхідні ролі для доступу
   */
  const hasRequiredRole = (): boolean => {
    if (requiredRole.length === 0) {
      return true; // Якщо ролі не вказані, доступ дозволений
    }
    
    return user?.role ? requiredRole.includes(user.role) : false;
  };

  // Стан завантаження - показуємо індикатор
  if (isLoading) {
    return (
      <div className="protected-route-loading">
        <LoadingSpinner size="large" />
        <p className="loading-text">Перевірка доступу...</p>
      </div>
    );
  }

  // Перевірка аутентифікації
  if (!isAuthenticated) {
    console.warn('🚫 Спроба доступу до захищеного маршруту без аутентифікації:', {
      path: location.pathname,
      redirectedTo: PUBLIC_ROUTES.HOME
    });

    return (
      <Navigate 
        to={PUBLIC_ROUTES.HOME} 
        replace 
        state={{ 
          from: location,
          message: 'Будь ласка, увійдіть в систему для доступу до цієї сторінки'
        }}
      />
    );
  }

  // Перевірка ролей (якщо потрібно)
  if (!hasRequiredRole()) {
    console.warn('🚫 Спроба доступу без відповідних прав:', {
      path: location.pathname,
      userRole: user?.role,
      requiredRoles: requiredRole
    });

    return (
      <ForbiddenPage
        message={accessDeniedMessage}
        showBackButton={false}
        showHomeButton={true}
      />
    );
  }

  // Якщо всі перевірки пройдені - рендеримо дочірній компонент з Layout
  return <Layout>{children}</Layout>;
};

export default ProtectedRoute;
